// react
import { useEffect, useRef } from 'react';

// relative
import type { DateRange } from '../types';
import useLiveMode from './useLiveMode';
import useLiveModeRange, { type LiveRangeMinutes } from './useLiveModeRange';

import { DateTime } from 'luxon';

const LIVEMODE_POLL_INTERVAL_MS = 10000; // 10 seconds

interface LiveModeManager {
  liveMode: boolean;
  liveRangeMinutes: LiveRangeMinutes;
  setLiveRangeMinutes: (minutes: LiveRangeMinutes) => void;
  toggleLiveMode: () => void;
}

/**
 * Custom hook to manage live mode state and interval polling.
 * Automatically sets up the interval when the page loads with liveMode=true in URL.
 *
 * Uses a ref to avoid stale closures in the setInterval callback — the callback
 * always reads the latest setDateRangeInUrl without needing it as an effect dep.
 */
function useLiveModeManager(
  dateRange: DateRange,
  setDateRangeInUrl: (
    range: DateRange,
    options?: { replace?: boolean },
  ) => void,
): LiveModeManager {
  const [liveMode, setLiveModeInUrl] = useLiveMode();
  const [liveRangeMinutes, setLiveRangeMinutes] = useLiveModeRange();
  const previousDateRangeRef = useRef<DateRange | null>(null);
  const windowAlreadySetRef = useRef(false);

  // Ref to always call the latest version of setDateRangeInUrl from the interval
  const setDateRangeRef = useRef(setDateRangeInUrl);
  useEffect(() => {
    setDateRangeRef.current = setDateRangeInUrl;
  });

  useEffect(() => {
    if (!liveMode) return;

    const updateDateRangeToLiveWindow = () => {
      const to = DateTime.now().plus({ minutes: 1 });
      const from = to.minus({ minutes: liveRangeMinutes });
      setDateRangeRef.current({ from, to }, { replace: true });
    };

    if (windowAlreadySetRef.current) {
      windowAlreadySetRef.current = false;
    } else {
      updateDateRangeToLiveWindow();
    }
    const intervalId = setInterval(
      updateDateRangeToLiveWindow,
      LIVEMODE_POLL_INTERVAL_MS,
    );

    return () => clearInterval(intervalId);
  }, [liveMode, liveRangeMinutes]);

  const getLiveWindow = (): DateRange => {
    const to = DateTime.now().plus({ minutes: 1 });
    return { from: to.minus({ minutes: liveRangeMinutes }), to };
  };

  const toggleLiveMode = () => {
    if (!liveMode) {
      previousDateRangeRef.current = dateRange;
      // Set the live window first so no fetch runs for the previous range.
      setDateRangeInUrl(getLiveWindow(), { replace: true });
      windowAlreadySetRef.current = true;
      setLiveModeInUrl(true);
      return;
    }

    setLiveModeInUrl(false);

    if (previousDateRangeRef.current) {
      setDateRangeInUrl(previousDateRangeRef.current, { replace: true });
      previousDateRangeRef.current = null;
    }
  };

  return {
    liveMode,
    liveRangeMinutes,
    setLiveRangeMinutes,
    toggleLiveMode,
  };
}

export default useLiveModeManager;
